import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/auth/AuthContext";
import { useAreaManagerSession } from "@/contexts/AreaManagerSessionContext";
import { isSlowOrDataSaverConnection } from "@/lib/connectionQuality";

const HEARTBEAT_MS = 15_000;
const HEARTBEAT_SLOW_MS = 60_000;
const ACTIVE_WINDOW_MS = 30_000;

function currentStoreFromUrl(search) {
  try {
    const params = new URLSearchParams(search || "");
    return {
      storeId: params.get("storeId") || "",
      storeName: params.get("storeName") || "",
    };
  } catch {
    return { storeId: "", storeName: "" };
  }
}

export function usePresenceHeartbeat() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { actingStoreId, actingStoreName } = useAreaManagerSession();

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;

    let cancelled = false;
    let lastActivityAt = Date.now();

    const markActivity = () => {
      lastActivityAt = Date.now();
    };

    const send = () => {
      if (cancelled) return;
      if (Date.now() - lastActivityAt > ACTIVE_WINDOW_MS) return;
      const fromUrl = currentStoreFromUrl(location.search);
      base44.presence
        .ping({
          path: `${location.pathname}${location.search}`,
          pageTitle: document?.title || "",
          storeId: fromUrl.storeId || actingStoreId || "",
          actingStoreId: fromUrl.storeId || actingStoreId || "",
          actingStoreName: fromUrl.storeName || actingStoreName || "",
        })
        .catch(() => {});
    };

    send();
    const intervalMs = isSlowOrDataSaverConnection() ? HEARTBEAT_SLOW_MS : HEARTBEAT_MS;
    const id = window.setInterval(send, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        markActivity();
        send();
      }
    };
    const activityEvents = ["pointerdown", "keydown", "scroll", "touchstart"];
    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, markActivity, { passive: true })
    );
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, markActivity)
      );
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [
    actingStoreId,
    actingStoreName,
    isAuthenticated,
    location.pathname,
    location.search,
    user?.id,
  ]);
}
